import {
  Breadcrumbs,
  Box,
  Typography,
  Link,
  Stack,
  Card,
  Avatar,
  Chip,
  Divider,
  AvatarGroup,
  Button,
} from "@mui/joy";
import { useTranslation } from "../../../i18n";
import {
  useChatConversationUserTeamCreateMutation,
  useGenralTeamSettingsQuery,
} from "../../../__generated__/types-and-hooks";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import OnlineUsersToggle from "../../../components/OnlineUsersToggle";
import DOMPurify from "dompurify";
import Email from "@mui/icons-material/EmailOutlined";
import WhatsApp from "@mui/icons-material/WhatsApp";
import Phone from "@mui/icons-material/LocalPhone";
import Message from "@mui/icons-material/Message";
import TelegramIcon from "@mui/icons-material/Telegram";
import LanguageIcon from "@mui/icons-material/Language"; // for website
import BusinessIcon from "@mui/icons-material/Business"; // for LinkedIn
import InstagramIcon from "@mui/icons-material/Instagram"; // optional
import FacebookIcon from "@mui/icons-material/Facebook";
import XIcon from "@mui/icons-material/X"; // for Twitter (now X)
import { ReactNode, useCallback } from "react";
import UserAvatar from "../../../components/UserAvatar";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";

// Map of contact method keys to corresponding icons
const iconMap: Record<string, ReactNode> = {
  phone: <Phone fontSize="small" />,
  email: <Email fontSize="small" />,
  whatsapp: <WhatsApp fontSize="small" />,
  telegram: <TelegramIcon fontSize="small" />,
  linkedin: <BusinessIcon fontSize="small" />,
  website: <LanguageIcon fontSize="small" />,
  twitter: <XIcon fontSize="small" />,
  facebook: <FacebookIcon fontSize="small" />,
  instagram: <InstagramIcon fontSize="small" />,
  other: <Message fontSize="small" />,
};

export default function TeamCard() {
  const t = useTranslation();
  const { teamSlug } = useParams();
  const genralTeamSettingsQuery = useGenralTeamSettingsQuery({
    variables: { slug: teamSlug! },
  });
  const navigate = useNavigate();

  const team = genralTeamSettingsQuery.data?.team_by_slug;
  const [startDirectConversation] = useChatConversationUserTeamCreateMutation();

  const handleMessage = useCallback(() => {
    if (!team) return;

    startDirectConversation({
      variables: {
        conversation: {
          recipientId: team.id,
        },
      },
      onCompleted(res) {
        navigate("/messages/" + res.chat_conversation_user_team_create.id);
      },
    });
  }, [startDirectConversation, team, navigate]);

  return (
    <>
      <Stack direction="row">
        <Breadcrumbs
          aria-label="breadcrumbs"
          sx={{
            "--Breadcrumbs-gap": "4px",
            m: 1,
          }}
          size="sm"
        >
          <Link component={RouterLink} color="neutral" to="/">
            <HomeIcon />
          </Link>
          <Link component={RouterLink} color="neutral" to="/network">
            <Typography level="title-sm">{t("Network")}</Typography>
          </Link>
          <Typography level="title-sm">{teamSlug}</Typography>
        </Breadcrumbs>
        <OnlineUsersToggle />
      </Stack>
      <Box sx={{ padding: 2, maxWidth: 900, margin: "0 auto" }}>
        <Card
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: 0,
            marginBottom: 3,
            boxShadow: "0px 0px 3px #000000",
            background: "var(surface)",
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: 220,
              width: "100%",
            }}
          >
            {team?.background && (
              <img
                src={team?.background}
                alt="Background"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  borderRadius: 8,
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0,
                }}
              />
            )}
          </Box>
          <Avatar
            sx={{
              position: "absolute",
              width: 200,
              height: 200,
              marginBottom: 2,
              boxShadow: "lg",
              marginTop: 0,
              top: 50,
              left: 20,
              border: 2,
              borderColor: "var(--joy-palette-background-level1)",
            }}
            src={team?.picture ?? "/images/qcsku_light.jpg"}
            alt={t("Team Logo")}
          />
          <Box
            gap={1}
            sx={{
              p: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "start",
            }}
          >
            <Box
              gap={1}
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "stretch",
                width: "100%",
              }}
            >
              <Stack gap={1} sx={{ flex: 1 }}>
                <Typography
                  endDecorator={
                    <Button
                      onClick={handleMessage}
                      startDecorator={<ForumOutlinedIcon />}
                    >
                      {t("Message")}
                    </Button>
                  }
                  level="h1"
                  fontWeight="bold"
                >
                  {team?.name}
                </Typography>
                <Typography level="body-md">{team?.description}</Typography>
              </Stack>
              <Stack gap={1}>
                {team?.contacts.map((method, index) => (
                  <Chip
                    key={index}
                    variant="soft"
                    sx={{ borderRadius: 4 }}
                    startDecorator={
                      iconMap[method.key] ?? (
                        <Message style={{ fontSize: 14 }} />
                      )
                    }
                  >
                    {method.value}
                  </Chip>
                ))}
              </Stack>
            </Box>
            <Box>
              <AvatarGroup>
                {team?.userEdges.map((edge) => (
                  <UserAvatar
                    key={edge.user.id}
                    userId={edge.user.id}
                    size="md"
                  />
                ))}
              </AvatarGroup>
            </Box>
            {/* About Section */}
            <Divider />
            <Stack gap={1} sx={{ alignItems: "left", width: "100%" }}>
              {team && team.about && (
                <Typography>
                  <div
                    className="quill-content"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(team.about),
                    }}
                  />
                </Typography>
              )}
            </Stack>
          </Box>
        </Card>
      </Box>
    </>
  );
}
